import { Calendar, type LucideIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import GenerativeContour from "@/components/primitives/GenerativeContour";
import SurfaceCard from "@/components/primitives/SurfaceCard";

interface EmptyTabProps {
  icon: LucideIcon;
  title: string;
  body: string;
  /** Optional CTA label — renders a teal "book a call" button wired to onCta. */
  cta?: string;
  onCta?: () => void;
  /** A muted line rendered centered below the card (e.g. the self-reported note). */
  subNote?: string;
}

/**
 * Level-1 empty — a whole tab with no data. A considered editorial "this chapter
 * is unwritten": a faint contour wash keeps it branded, never void. Copy is passed
 * in per-tab and is never borrowed from another tab.
 */
const EmptyTab = ({ icon: Icon, title, body, cta, onCta, subNote }: EmptyTabProps) => (
  <div>
    <SurfaceCard padding="none" className="relative overflow-hidden rounded-2xl">
      {/* faint contour wash so the empty still feels branded, not void */}
      <div
        className="pointer-events-none absolute inset-0 opacity-50"
        style={{
          maskImage: "radial-gradient(120% 100% at 50% 0%, rgba(0,0,0,0.18), transparent 62%)",
          WebkitMaskImage: "radial-gradient(120% 100% at 50% 0%, rgba(0,0,0,0.18), transparent 62%)",
        }}
      >
        <GenerativeContour name={title} variant="cover" rings={18} className="h-full w-full" />
      </div>

      <div className="relative flex flex-col items-center px-10 py-16 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-tint">
          <Icon className="h-6 w-6 text-primary" strokeWidth={1.7} />
        </div>
        <h3 className="mt-5 font-display text-[23px] font-semibold leading-[1.15] text-ink-900">{title}</h3>
        <p className="mx-auto mt-[11px] max-w-[440px] text-[15px] leading-[1.6] text-ink-700">{body}</p>
        {cta && (
          <div className="mt-[22px]">
            <Button size="lg" className="gap-2" onClick={onCta}>
              <Calendar className="h-4 w-4" />
              {cta}
            </Button>
          </div>
        )}
      </div>
    </SurfaceCard>

    {subNote && (
      <div className="mt-3.5 text-center">
        <span className="text-[12.5px] text-ink-500">{subNote}</span>
      </div>
    )}
  </div>
);

export default EmptyTab;
